import { isNullOrUndefined } from 'rewire-common';
import { archiveUnArchive } from '../graphql/Mutations';
import { getUdfSortLookupString } from '../utility/DataConverters';
import EditDialogModel, { TEditSaveFn } from '../schedule/EditDialogModel';
// import { IUdf }                              from '../DataTypes';
import SettingsModel, { IOtherField, ISettingsOptionsMenu } from './SettingsModel';
import PagingModel from './PagingModel';
import udfsSubscriptionService from '../services/UdfsSubscriptionService';
import PrioritySortMenuModel from '../ui/PrioritySortMenuModel';
import { ISort, SortInput, UdfDocumentType } from '../DataTypes';

export function getPermittedSorts(
  sorts: ISort | undefined,
  documentType: UdfDocumentType,
  permittedKeys: string[] = [],
): ISort | undefined {
  const permittedSorts: ISort = {};
  if (sorts) {
    for (const key of Object.keys(sorts) as (keyof ISort)[]) {
      if (key === 'custom') {
        const customSorts = sorts![key]?.map((customSort: SortInput) => ({ ...customSort }) as SortInput);
        const permittedCustomSorts = customSorts?.filter((sortInput) => permittedKeys.includes(sortInput.field));
        if (permittedCustomSorts?.length) {
          for (const sortInput of permittedCustomSorts) {
            const isUdf = !isNullOrUndefined(udfsSubscriptionService.subscriptionModel.getUdfByName(sortInput.field));
            if (!isUdf) continue;
            sortInput.field = getUdfSortLookupString(sortInput.field, documentType);
          }
          permittedSorts[key] = permittedCustomSorts;
        }
      } else {
        if (permittedKeys.includes(key)) {
          permittedSorts[key] = sorts![key];
        }
      }
    }
  }

  if (!Object.keys(permittedSorts).length) {
    return undefined;
  }

  return permittedSorts;
}

export class MasterDataSettingsModel extends SettingsModel<{ archived: string }> {
  public constructor(
    public title: string,
    documentType?: UdfDocumentType,
    editor?: EditDialogModel,
    public otherFields?: IOtherField[],
    public optionsMenu?: ISettingsOptionsMenu,
    protected _update?: TEditSaveFn,
    onSelectedItem?: any,
  ) {
    super(title, documentType, editor, otherFields, optionsMenu, _update, onSelectedItem);
    if (!documentType) return;

    function load(_filter?: { search?: string }): Promise<{ cursor?: string; totalCount: number }> {
      const permittedKeys = this.sortModel.getSortMenuItems().map((i: any) => i.name);
      const filter = Object.assign({}, _filter, { archived: this.state.archived });
      const sorts = getPermittedSorts(this.sortModel.sorts, this._documentType, permittedKeys);
      return this._load(filter, sorts);
    }

    this.filterMenuItems = [
      {
        name: 'noArchived',
        title: 'Unarchived Only',
        onClick: () => (this.archived = 'none'),
        active: () => this.state.archived === 'none',
        closeOnClick: true,
      },
      {
        name: 'archivedOnly',
        title: 'Archived Only',
        onClick: () => (this.archived = 'only'),
        active: () => this.state.archived === 'only',
        closeOnClick: true,
      },
      {
        name: 'archivedInclude',
        title: 'All',
        onClick: () => (this.archived = 'include'),
        active: () => this.state.archived === 'include',
        closeOnClick: true,
      },
    ];

    this.state.archived = 'none';
    this.paging = new PagingModel(load.bind(this));
    this.sortModel = new PrioritySortMenuModel(
      () => this.paging.load(true),
      () => this.getSortMenuItems(),
    );
  }

  getDefaultEditPanelHeaderOptionsItems() {
    const optionsItems = super.getDefaultEditPanelHeaderOptionsItems();
    optionsItems.push(
      ...[
        {
          name: 'archive',
          title: 'Archive',
          onClick: this.archive,
          closeOnClick: true,
          disabled: () => !this._canEditItem || this.hasChanges || !this.selectedItem || this.selectedItem.archived,
        },
        {
          name: 'unArchive',
          title: 'Unarchive',
          onClick: this.unArchive,
          closeOnClick: true,
          disabled: () => !this._canEditItem || this.hasChanges || !this.selectedItem || !this.selectedItem.archived,
        },
      ],
    );

    return optionsItems;
  }

  getOtherSortMenuItems(): { name: string; title: string; isCustom?: boolean }[] {
    return [];
  }

  getSortMenuItems(): { name: string; title: string; isCustom?: boolean }[] {
    return [
      { name: 'name', title: 'Name' },
      { name: 'code', title: 'Code' },
      ...this.getOtherSortMenuItems(),
      // ...udfsSubscriptionService.subscriptionModel.getUdfsByDocumentType(this._documentType).map((udf: IUdf) => (
      //   {name: udf.name, title: udf.displayName, isCustom: true}
      // ))
    ];
  }

  public set archived(value: string) {
    this.state.archived = value;
    this.paging.load(true);
  }

  archive = async () => {
    return this.run('archive');
  };

  unArchive = async () => {
    return this.run('unArchive');
  };

  async run(fn: 'archive' | 'unArchive') {
    await archiveUnArchive(fn, this._documentType, this.selectedItem);
    this.paging.load(true);
  }
}
